import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { REASON_OPTIONS, VERDICT_LABEL } from '@/lib/format';
import { toast } from 'sonner';

const VERDICTS = [
  { value: 'correct', icon: ThumbsUp, cls: 'data-[on=true]:bg-money data-[on=true]:text-white data-[on=true]:border-money' },
  { value: 'needs_improvement', icon: AlertCircle, cls: 'data-[on=true]:bg-warn data-[on=true]:text-white data-[on=true]:border-warn' },
  { value: 'incorrect', icon: ThumbsDown, cls: 'data-[on=true]:bg-danger data-[on=true]:text-white data-[on=true]:border-danger' },
];

export const FeedbackControl = ({ event, onSubmitted }) => {
  const [verdict, setVerdict] = useState(null);
  const [wouldSubmit, setWouldSubmit] = useState(null);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const isChange = event?.is_scope_change;
  const needsReason = verdict === 'incorrect' || verdict === 'needs_improvement';

  const submit = async () => {
    if (!verdict) { toast.error('Select a verdict'); return; }
    setLoading(true);
    try {
      await api.post('/feedback', {
        event_id: event.id, verdict,
        would_submit_co: wouldSubmit || undefined,
        reason_code: needsReason ? (reason || undefined) : undefined,
        comment: comment || undefined,
      });
      toast.success('Feedback saved — thank you');
      onSubmitted && onSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save feedback'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-3" data-testid="feedback-control">
      <div>
        <Label className="text-xs font-semibold text-slate-600">Do you agree with the AI?</Label>
        <div className="grid grid-cols-3 gap-2 mt-1.5">
          {VERDICTS.map((v) => {
            const Icon = v.icon;
            return (
              <button key={v.value} type="button" data-on={verdict === v.value} data-testid={`verdict-${v.value}`}
                onClick={() => setVerdict(v.value)}
                className={cn('flex flex-col items-center gap-1 rounded-lg border border-slate-200 py-2.5 text-xs font-medium text-slate-600 transition-colors hover:border-slate-300', v.cls)}>
                <Icon className="h-4 w-4" />{VERDICT_LABEL[v.value]}
              </button>
            );
          })}
        </div>
      </div>

      {isChange && (
        <div>
          <Label className="text-xs font-semibold text-slate-600">Would you submit this change order?</Label>
          <div className="grid grid-cols-3 gap-2 mt-1.5">
            {['yes', 'unsure', 'no'].map((w) => (
              <button key={w} type="button" data-on={wouldSubmit === w} data-testid={`would-submit-${w}`}
                onClick={() => setWouldSubmit(w)}
                className={cn('rounded-lg border border-slate-200 py-2 text-xs font-medium capitalize text-slate-600 transition-colors hover:border-slate-300',
                  'data-[on=true]:bg-ink-900 data-[on=true]:text-white data-[on=true]:border-ink-900')}>{w}</button>
            ))}
          </div>
        </div>
      )}

      {needsReason && (
        <div>
          <Label className="text-xs font-semibold text-slate-600">Main reason (optional)</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="mt-1.5" data-testid="feedback-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
            <SelectContent>{REASON_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label className="text-xs font-semibold text-slate-600">Comment (optional)</Label>
        <Textarea className="mt-1.5" rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Anything the AI got right or wrong..." data-testid="feedback-comment" />
      </div>

      <Button onClick={submit} disabled={loading} className="w-full bg-brand hover:bg-brand-600" data-testid="feedback-submit">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Submit feedback
      </Button>
    </div>
  );
};
